import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Pagination,
  TableSortLabel,
  useTheme,
  Chip,
  IconButton
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useListInvoicesByStore } from "../../hooks/api/useInvoice";
import NoInvoiceFound from "./NoInvoiceFound";
import InvoiceDetailModal from "./InvoiceDetailModal";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { formatDate, getPaymentStatusColor } from "../Orders/utils/orderUtils";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import ListPageToolbar from "../common/ListPageToolbar";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useReportError } from "../../hooks/useReportError";
import { SortableInvoiceKey } from "./interfaces/sortableInvoiceKey";

type SortOrder = "asc" | "desc";

const InvoiceList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const formatCurrency = useFormatCurrency();

  const { invoiceList } = useSelector((state: RootState) => state.invoices);
  const listInvoicesByStore = useListInvoicesByStore();
  const reportError = useReportError();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  /** First list fetch only — avoids empty-state flash and matches SalesOrderList. */
  const [fetchDone, setFetchDone] = useState(false);

  const [search, setSearch] = useState("");
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [orderBy, setOrderBy] = useState<SortableInvoiceKey>("issueDate");
  const itemsPerPage = 10;

  useEffect(() => {
    (async () => {
      try {
        await listInvoicesByStore.mutateAsync();
      } catch (err) {
        reportError("Failed to fetch invoice list (UI)", err, {
          component: "InvoiceList"
        });
      } finally {
        setFetchDone(true);
      }
    })();
  }, [reportError]);

  const filteredList = useMemo(() => {
    if (!search.trim()) return invoiceList;
    const q = search.toLowerCase();
    return invoiceList.filter((invoice) => {
      const statusStr = invoice.status.toLowerCase();
      const issueFmt = formatDate(invoice.issueDate).toLowerCase();
      return (
        statusStr.includes(q) ||
        issueFmt.includes(q) ||
        invoice.issueDate.toLowerCase().includes(q) ||
        String(invoice.totalAmount).includes(q) ||
        String(invoice.paidAmount).includes(q) ||
        String(invoice.remainingAmount).includes(q)
      );
    });
  }, [invoiceList, search]);

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (orderBy === "issueDate") {
        const aDate = new Date(String(aValue)).getTime();
        const bDate = new Date(String(bValue)).getTime();
        return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
      }
      return sortOrder === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, sortOrder, orderBy]);

  const paginatedList = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedList.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedList, page]);

  const handleSort = (property: SortableInvoiceKey) => {
    const isAsc = orderBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  if (!fetchDone) {
    return (
      <CommonLoader
        loadingMessage={t("invoice-list-loading", "Loading invoices...")}
      />
    );
  }

  if (invoiceList.length === 0) {
    return <NoInvoiceFound />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <ListPageToolbar
        title={t("invoices", "Invoices")}
        icon={
          <DescriptionOutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 22 }} />
        }
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t(
          "search-invoices-placeholder",
          "Search by amount, issue date, or status."
        )}
        isMobile={matchMD}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "totalAmount"}
                  direction={orderBy === "totalAmount" ? sortOrder : "asc"}
                  onClick={() => handleSort("totalAmount")}
                >
                  {t("total-amount", "Total")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "paidAmount"}
                  direction={orderBy === "paidAmount" ? sortOrder : "asc"}
                  onClick={() => handleSort("paidAmount")}
                >
                  {t("paid-amount", "Paid")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "remainingAmount"}
                  direction={orderBy === "remainingAmount" ? sortOrder : "asc"}
                  onClick={() => handleSort("remainingAmount")}
                >
                  {t("remaining-amount", "Remaining")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "issueDate"}
                  direction={orderBy === "issueDate" ? sortOrder : "asc"}
                  onClick={() => handleSort("issueDate")}
                >
                  {t("issue-date", "Issue Date")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? sortOrder : "asc"}
                  onClick={() => handleSort("status")}
                >
                  {t("status", "Status")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">{t("actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedList.map((invoice) => (
              <TableRow key={invoice.invoiceId} hover>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatCurrency(invoice.totalAmount)}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatCurrency(invoice.paidAmount)}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 13,
                    color:
                      invoice.remainingAmount > 0
                        ? "error.main"
                        : "success.main",
                    fontWeight: "medium"
                  }}
                >
                  {formatCurrency(invoice.remainingAmount)}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatDate(invoice.issueDate)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={invoice.status}
                    color={getPaymentStatusColor(invoice.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 13 }}>
                  <IconButton
                    color="primary"
                    aria-label={t("view-invoice", "View Invoice")}
                    onClick={() => setDetailInvoiceId(invoice.invoiceId)}
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedList.length > itemsPerPage && (
        <Stack spacing={2} alignItems="center" mt={3}>
          <Pagination
            count={Math.ceil(sortedList.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      <InvoiceDetailModal
        open={detailInvoiceId !== null}
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
      />
    </Box>
  );
};

export default InvoiceList;
